import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupsIcon from "@mui/icons-material/Groups";
import StoreIcon from "@mui/icons-material/Store";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomSnackbar from "../components/CustomSnackbar";
import useTradeDelay from "./TradeDelay/hooks/useTradeDelay";
import BasketsTab from "./TradeDelay/components/BasketsTab";
import TeamManagementTab from "./TradeDelay/components/TeamManagementTab";
import BranchManagementTab from "./TradeDelay/components/BranchManagementTab";
import { useUser } from "../contexts/UserContext";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("he-IL");
};

const formatNum = (n) =>
  n != null && n !== 0 ? Number(n).toFixed(2) : n === 0 ? "0" : "—";

const DISCOUNT_FIELDS = [
  { key: "NEO_CONSTDISC", label: "הנחה קבועה" },
  { key: "NEO_AGENTDISC", label: "הנחת סוכן" },
  { key: "NEO_SALEDISC", label: "הנחת מבצע" },
  { key: "NEO_NEWPARTDISC", label: "הנחת מוצר חדש" },
  { key: "NEO_MARKDISC", label: "הנחת יעדים" },
  { key: "NEO_ORIGDISC", label: "הנחה מקורית" },
  { key: "NEO_EDIPERCENT", label: "הנחת ממשק" },
];

const MAIN_COLS = 8;

// ─── Item Row with expandable discounts ──────────────────────
// extraCells: optional React fragment of <TableCell> elements to replace the PRICE cell
const ItemRow = ({ item, extraCells }) => {
  const [open, setOpen] = useState(false);
  const colSpan = extraCells ? 10 : 6;
  const hasAgentException = !!item.NEO_CHECKAGENTDISC;

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: "pointer",
          bgcolor: hasAgentException
            ? open
              ? "#fee2e2"
              : "#fef2f2" // ← אדום אם חריגת סוכן
            : open
              ? "#f0f7ff"
              : "transparent",
          "& > *": {
            borderBottom: open ? "none" : "1px solid rgba(0,0,0,0.05)",
          },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 36, pr: 0 }}>
          <IconButton
            size="small"
            sx={{ color: hasAgentException ? "#dc2626" : "#0054A5" }}
          >
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>

        {/* מק"ט — אדום + tooltip אם יש חריגה */}
        <TableCell
          sx={{
            fontWeight: 600,
            color: hasAgentException ? "#dc2626" : "#0054A5",
          }}
        >
          {hasAgentException ? (
            <Tooltip title="קיימת חריגת סוכן">
              <span>{item.PARTNAME}</span>
            </Tooltip>
          ) : (
            item.PARTNAME
          )}
        </TableCell>

        <TableCell
          title={item.PDES}
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.PDES}
        </TableCell>
        <TableCell align="center">{item.BARCODE}</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          {item.TQUANT}
        </TableCell>
        {extraCells ? (
          extraCells
        ) : (
          <TableCell align="center">{formatNum(item.PRICE)}</TableCell>
        )}
      </TableRow>

      {/* Discounts expanded row */}
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ p: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: hasAgentException ? "#fff5f5" : "#f8fafc",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                overflowX: "auto",
              }}
            >
              {hasAgentException && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: "#dc2626",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                >
                  קיימת חריגת סוכן בפריט זה
                </Typography>
              )}
              <Typography
                variant="caption"
                fontWeight={700}
                color="#6b7280"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                הנחות מוצר
              </Typography>
              <Table
                size="small"
                sx={{
                  "& td, & th": {
                    px: 1.5,
                    py: 0.6,
                    fontSize: "0.75rem",
                    border: "none",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {DISCOUNT_FIELDS.map((d) => (
                      <TableCell
                        key={d.key}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          color:
                            d.key === "NEO_AGENTDISC" && hasAgentException
                              ? "#dc2626"
                              : "#6b7280",
                        }}
                      >
                        {d.label} %
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{ bgcolor: hasAgentException ? "#fee2e2" : "#f0f7ff" }}
                  >
                    {DISCOUNT_FIELDS.map((d) => (
                      <TableCell
                        key={d.key}
                        align="center"
                        sx={{
                          fontWeight: item[d.key] > 0 ? 700 : 400,
                          color:
                            d.key === "NEO_AGENTDISC" && hasAgentException
                              ? "#dc2626"
                              : item[d.key] > 0
                                ? "#0054A5"
                                : "#9ca3af",
                        }}
                      >
                        {item[d.key] > 0 ? `${formatNum(item[d.key])}%` : "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const DiscountChip = ({ label, value, highlight, active, color }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      bgcolor: highlight
        ? color === "#217346"
          ? "#f0faf4"
          : "#ebf7ff"
        : active
          ? "#f5f5f5"
          : "#fafafa",
      border: `1px solid ${highlight ? (color === "#217346" ? "#c6ecd4" : "#c5def7") : active ? "#e0e0e0" : "#f0f0f0"}`,
      borderRadius: 2,
      px: 1.5,
      py: 0.5,
      minWidth: 80,
    }}
  >
    <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.65rem" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={highlight || active ? 700 : 400}
      color={color || (active ? "#1f2937" : "#9ca3af")}
      sx={{ fontSize: "0.8rem" }}
    >
      {value}
    </Typography>
  </Box>
);

const LastSalesDialog = ({
  open,
  onClose,
  custName,
  partName,
  fetchLastSales,
}) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);

  // טעינה ראשונית כשהדיאלוג נפתח
  useEffect(() => {
    if (!open) return;
    setSales([]);
    setSkip(0);
    setHasMore(true);
    loadSales(0, true);
  }, [open, custName, partName]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSales = async (currentSkip, isFirst = false) => {
    isFirst ? setLoading(true) : setLoadingMore(true);
    try {
      const results = await fetchLastSales(custName, partName, currentSkip);
      setSales((prev) => (isFirst ? results : [...prev, ...results]));
      setHasMore(results.length === 10);
      setSkip(currentSkip + results.length);
    } catch {
      setHasMore(false);
    } finally {
      isFirst ? setLoading(false) : setLoadingMore(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir="rtl"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ bgcolor: "#ebf7ff", px: 3, pt: 3, pb: 1.5 }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <HistoryIcon sx={{ color: "#0054A5" }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" color="#1f2937">
                  הזמנות אחרונות
                </Typography>
                <Typography variant="caption" color="#6b7280">
                  מק"ט: {partName} | לקוח: {custName}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "#6b7280" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : sales.length === 0 ? (
          <Typography color="#6b7280" textAlign="center" py={3}>
            לא נמצאו הזמנות קודמות למוצר זה
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#ebf7ff" }}>
                    {[
                      "מס' הזמנה",
                      "תאריך",
                      "סטטוס",
                      "כמות",
                      "מחיר ליחידה",
                      "מחיר אחרי הנחה",
                      "הנחה %",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.78rem",
                          color: "#1f2937",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.map((s, i) => (
                    <TableRow
                      key={i}
                      hover
                      sx={{ "& > *": { fontSize: "0.82rem" } }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#0054A5" }}>
                        {s.ORDNAME}
                      </TableCell>
                      <TableCell>{formatDate(s.CURDATE)}</TableCell>
                      <TableCell>
                        <Chip
                          label={s.ORDSTATUSDES}
                          size="small"
                          sx={{ fontSize: "0.68rem", height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        {s.TQUANT}
                      </TableCell>
                      <TableCell align="center">{formatNum(s.PRICE)}</TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 600, color: "#217346" }}
                      >
                        {formatNum(s.DISPRICE)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#0054A5" }}>
                        {formatNum(s.PERCENT)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Load More */}
            {hasMore && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => loadSales(skip)}
                  disabled={loadingMore}
                  startIcon={
                    loadingMore ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                  sx={{
                    borderColor: "#0054A5",
                    color: "#0054A5",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#ebf7ff" },
                  }}
                >
                  {loadingMore ? "טוען..." : `טען עוד 10`}
                </Button>
              </Box>
            )}

            {!hasMore && sales.length > 0 && (
              <Typography
                variant="caption"
                color="#9ca3af"
                display="block"
                textAlign="center"
                mt={1.5}
              >
                הוצגו כל {sales.length} ההזמנות
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
// ─── Smart status suggestion ──────────────────────────────────
const getSuggestedStatus = (order) => {
  // Order-level rule: an obligo exception on the order always forces a draft
  // status, regardless of any other line-level condition.
  if (order.NEO_OBLIGOEXIST === "Y") return "טיוטא פרומדי";
  const items = order?.ORDERITEMS_SUBFORM || [];
  if (items.some((i) => i.PDES?.includes("SIMILAC"))) return "נדרש פיצול";
  return "מאושר פרומדי";
};

// ─── Expandable Row (manual release) ─────────────────────────
const OrderRow = ({
  order,
  releaseStatuses,
  onUpdateStatus,
  updateOrderItem,
  fetchLastSales,
}) => {
  const [open, setOpen] = useState(false);
  const suggestedStatus = getSuggestedStatus(order);
  // Obligo exception on the order forces "טיוטא פרומדי" — Priority itself
  // rejects any other status in that case, so don't even offer them here.
  const availableStatuses =
    order.NEO_OBLIGOEXIST === "Y" ? ["טיוטא פרומדי"] : releaseStatuses;
  // Only pre-select the suggestion if it's actually an allowed status for this
  // user (e.g. external suppliers can't select "נדרש פיצול").
  const [selectedStatus, setSelectedStatus] = useState(
    availableStatuses.includes(suggestedStatus) ? suggestedStatus : "",
  );
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Per-item editing state
  const [editingKeys, setEditingKeys] = useState(new Set()); // string indices
  const [edits, setEdits] = useState({}); // { [idxStr]: { NEO_AGENTDISC? } }
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Last sales popup state
  const [salesDialog, setSalesDialog] = useState({
    open: false,
    custName: "",
    partName: "",
  });

  const openLastSales = (partName) => {
    setSalesDialog({ open: true, custName: order.CUSTNAME, partName });
  };

  const items = order?.ORDERITEMS_SUBFORM || [];

  // Dirty = any item in edit mode with at least one changed field not yet saved
  const hasDirty = [...editingKeys].some(
    (k) => !savedKeys.has(k) && edits[k] && Object.keys(edits[k]).length > 0,
  );

  const toggleEdit = (k, e) => {
    e.stopPropagation();
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
        // Discard unsaved changes for this item
        if (!savedKeys.has(k)) {
          setEdits((prev2) => {
            const ne = { ...prev2 };
            delete ne[k];
            return ne;
          });
        }
      } else {
        next.add(k);
      }
      return next;
    });
    setSaveError(null);
  };

  const handleFieldChange = (k, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || {}), [field]: value },
    }));
    setSavedKeys((prev) => {
      const s = new Set(prev);
      s.delete(k);
      return s;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      for (const k of editingKeys) {
        if (savedKeys.has(k)) continue;
        const edit = edits[k] || {};
        const updates = {};
        if (edit.NEO_AGENTDISC !== undefined && edit.NEO_AGENTDISC !== "")
          updates.NEO_AGENTDISC = Number(edit.NEO_AGENTDISC);
        if (Object.keys(updates).length > 0 && updateOrderItem) {
          const item = items[Number(k)];
          if (item?.KLINE != null) {
            await updateOrderItem(order.ORDNAME, item.KLINE, updates);
          }
        }
        setSavedKeys((prev) => new Set([...prev, k]));
      }
      setEditingKeys(new Set());
      // Keep edits as display overrides so the saved values remain visible
    } catch (err) {
      setSaveError(err?.message || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedStatus) return;
    setUpdating(true);
    setUpdateError(null);
    try {
      await onUpdateStatus(order.ORDNAME, selectedStatus);
      setOpen(false);
      setSelectedStatus("");
    } catch (err) {
      setUpdateError(err?.msg || "שגיאה בעדכון סטטוס ההזמנה");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      {/* Main Row */}
      <TableRow
        hover
        sx={{
          cursor: "pointer",
          transition: "background 0.2s",
          bgcolor: open ? "#f0f7ff" : "transparent",
          "& > *": {
            borderBottom: open ? "none" : "1px solid rgba(0,0,0,0.06)",
          },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 48, pr: 0 }}>
          <IconButton size="small" sx={{ color: "#0054A5" }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: "#0054A5" }}>
          {order.ORDNAME}
        </TableCell>
        <TableCell>{order.CDES}</TableCell>
        <TableCell>{formatDate(order.CURDATE)}</TableCell>
        <TableCell>{formatDate(order.NEO_DUEDATE)}</TableCell>
        <TableCell>{order.AGENTNAME}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={order.NEO_OBLIGOEXIST === "Y" ? "קיימת חריגת אובליגו" : "אין חריגת אובליגו"}>
            <span>
              <Checkbox
                size="small"
                checked={order.NEO_OBLIGOEXIST === "Y"}
                disabled
                sx={{
                  p: 0,
                  "&.Mui-disabled": {
                    color: order.NEO_OBLIGOEXIST === "Y" ? "#dc2626" : "#c7c7c7",
                  },
                }}
              />
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            label={order.ORDSTATUSDES}
            size="small"
            sx={{
              bgcolor: "#fff3e0",
              color: "#e65100",
              fontWeight: 600,
              fontSize: "0.72rem",
            }}
          />
        </TableCell>
      </TableRow>

      {/* Expanded Row */}
      <TableRow>
        <TableCell colSpan={MAIN_COLS} sx={{ p: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: "#f8fafc",
                borderBottom: "2px solid #ebf7ff",
                px: 3,
                py: 2,
              }}
            >
              {/* Items Table */}
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="#1f2937"
                mb={1}
              >
                פריטי הזמנה ({items.length})
              </Typography>

              <Box
                bgcolor="#ffffff"
                borderRadius={2}
                border="1px solid rgba(0,0,0,0.08)"
                mb={2.5}
              >
                <TableContainer sx={{ maxHeight: 400, borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 36, bgcolor: "#ebf7ff" }} />
                        {[
                          "מקט",
                          "תיאור",
                          "ברקוד",
                          "כמות",
                          "מחיר ליחידה",
                          "מחיר לאחר הנחה",
                          "הנחת סוכן %",
                          "הנחה כוללת %",
                          "",
                        ].map((col) => (
                          <TableCell
                            key={col}
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              bgcolor: "#ebf7ff",
                              color: "#1f2937",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            align="center"
                            sx={{ py: 3, color: "#6b7280" }}
                          >
                            אין פריטים
                          </TableCell>
                        </TableRow>
                      )}
                      {items.map((item, idx) => {
                        const k = String(idx);
                        const isEditing = editingKeys.has(k);
                        const isSaved = savedKeys.has(k);
                        const edit = edits[k] || {};

                        const extraCells = (
                          <>
                            <TableCell
                              align="center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography variant="body2" sx={{ color: "#1f2937" }}>
                                {formatNum(item.PRICE)}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography variant="body2" sx={{ color: "#217346", fontWeight: 600 }}>
                                {formatNum(item.DISPRICE)}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                bgcolor: isEditing ? "#f0f7ff" : "transparent",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={
                                    edit.NEO_AGENTDISC ??
                                    item.NEO_AGENTDISC ??
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleFieldChange(
                                      k,
                                      "NEO_AGENTDISC",
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      fontSize: "0.78rem",
                                      padding: "3px 6px",
                                      width: 60,
                                    },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                    },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isSaved ? "#217346" : "#1f2937",
                                    fontWeight: isSaved ? 700 : 400,
                                  }}
                                >
                                  {formatNum(
                                    edit.NEO_AGENTDISC ?? item.NEO_AGENTDISC,
                                  )}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography variant="body2" sx={{ color: "#0054A5", fontWeight: 600 }}>
                                {formatNum(item.PERCENT)}%
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Tooltip
                                  title={
                                    isEditing ? "בטל עריכה" : "ערוך מחיר / הנחה"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => toggleEdit(k, e)}
                                    sx={{
                                      color: isEditing ? "#dc2626" : "#6b7280",
                                      "&:hover": {
                                        color: isEditing
                                          ? "#b91c1c"
                                          : "#0054A5",
                                      },
                                    }}
                                  >
                                    {isEditing ? (
                                      <CloseIcon fontSize="small" />
                                    ) : (
                                      <EditIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="מכירות אחרונות">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openLastSales(item.PARTNAME);
                                    }}
                                    sx={{
                                      color: "#6b7280",
                                      "&:hover": { color: "#0054A5" },
                                    }}
                                  >
                                    <HistoryIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </>
                        );

                        return (
                          <ItemRow
                            key={idx}
                            item={item}
                            extraCells={extraCells}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {saveError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mb: 1 }}
                >
                  {saveError}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Action area — changes based on dirty state */}
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                {hasDirty ? (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#e65100"
                    >
                      יש שינויים לא שמורים — שמור לפני שחרור
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      startIcon={
                        saving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      sx={{
                        bgcolor: "#0054A5",
                        borderRadius: 2,
                        px: 3,
                        "&:hover": { bgcolor: "#003d7a" },
                      }}
                    >
                      {saving ? "שומר..." : "שמור עדכונים ב-Priority"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#1f2937"
                    >
                      שחרור הזמנה:
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>בחר סטטוס לשחרור</InputLabel>
                        <Select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                          label="בחר סטטוס לשחרור"
                          sx={{ bgcolor: "#ffffff", borderRadius: 2 }}
                        >
                          {availableStatuses.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedStatus === suggestedStatus && (
                        <Typography
                          variant="caption"
                          color="#6b7280"
                          sx={{ fontSize: "0.68rem" }}
                        >
                          ✦ מוצע אוטומטית
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={handleUpdate}
                      disabled={!selectedStatus || updating}
                      startIcon={
                        updating ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleOutlineIcon />
                        )
                      }
                      sx={{
                        bgcolor: "#0054A5",
                        borderRadius: 2,
                        px: 3,
                        "&:hover": { bgcolor: "#003d7a" },
                      }}
                    >
                      {updating ? "מעדכן..." : "עדכן סטטוס"}
                    </Button>
                  </>
                )}
              </Box>
              {updateError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 1 }}
                >
                  {updateError}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <LastSalesDialog
        open={salesDialog.open}
        onClose={() => setSalesDialog((prev) => ({ ...prev, open: false }))}
        custName={salesDialog.custName}
        partName={salesDialog.partName}
        fetchLastSales={fetchLastSales}
      />
    </>
  );
};

// ─── Preview Order Row (auto-release dialog, with inline item editing) ────────
const PreviewOrderRow = ({
  order,
  checked,
  onCheck,
  onDirtyChange,
  updateOrderItem,
}) => {
  const [open, setOpen] = useState(false);
  const [editingKeys, setEditingKeys] = useState(new Set()); // string indices
  const [edits, setEdits] = useState({});
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const items = order.items || [];

  // Dirty = edit mode open with unsaved changes
  const hasDirty = [...editingKeys].some(
    (k) => !savedKeys.has(k) && edits[k] && Object.keys(edits[k]).length > 0,
  );

  // Inform parent whenever dirty state changes
  useEffect(() => {
    onDirtyChange(order.ordname, hasDirty);
  }, [hasDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleEdit = (k, e) => {
    e.stopPropagation();
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
        if (!savedKeys.has(k)) {
          setEdits((prev2) => {
            const ne = { ...prev2 };
            delete ne[k];
            return ne;
          });
        }
      } else {
        next.add(k);
      }
      return next;
    });
    setSaveError(null);
  };

  const handleFieldChange = (k, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || {}), [field]: value },
    }));
    setSavedKeys((prev) => {
      const s = new Set(prev);
      s.delete(k);
      return s;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      for (const k of editingKeys) {
        if (savedKeys.has(k)) continue;
        const edit = edits[k] || {};
        const updates = {};
        if (edit.NEO_AGENTDISC !== undefined && edit.NEO_AGENTDISC !== "")
          updates.NEO_AGENTDISC = Number(edit.NEO_AGENTDISC);
        if (Object.keys(updates).length > 0 && updateOrderItem) {
          const item = items[Number(k)];
          const kline = item?.kline ?? item?.KLINE;
          if (kline != null) {
            await updateOrderItem(order.ordname, kline, updates);
          }
        }
        setSavedKeys((prev) => new Set([...prev, k]));
      }
      setEditingKeys(new Set());
      // Keep edits as display overrides so the saved values remain visible
    } catch (err) {
      setSaveError(err?.message || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ListItem disablePadding sx={{ py: 0.3 }}>
        <Tooltip title={hasDirty ? "שמור שינויים לפני הסימון" : ""}>
          <span>
            <Checkbox
              size="small"
              checked={checked}
              disabled={hasDirty}
              onChange={(e) => onCheck(order.ordname, e.target.checked)}
            />
          </span>
        </Tooltip>
        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
        >
          <strong>{order.ordname}</strong>
          <Typography variant="caption" color="#6b7280">
            ({order.custName})
          </Typography>
          <Chip
            label={order.newStatus}
            size="small"
            sx={{
              bgcolor: "#ebf7ff",
              color: "#0054A5",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
          <Typography variant="caption" color="#9ca3af">
            סל: {order.basket}
          </Typography>
          {hasDirty && (
            <Chip
              label="יש שינויים לא שמורים"
              size="small"
              sx={{
                bgcolor: "#fff3e0",
                color: "#e65100",
                fontSize: "0.65rem",
                height: 18,
              }}
            />
          )}
        </Box>
        {items.length > 0 && (
          <Tooltip title={open ? "סגור פריטים" : "ערוך פריטים"}>
            <IconButton
              size="small"
              onClick={() => setOpen((v) => !v)}
              sx={{ color: "#0054A5" }}
            >
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </ListItem>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 4, pr: 1, pb: 1.5 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                  {[
                    "מקט",
                    "תיאור",
                    "כמות",
                    "מחיר ליחידה",
                    "הנחת סוכן %",
                    "",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        py: 0.7,
                        color: "#374151",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => {
                  const k = String(idx);
                  const isEditing = editingKeys.has(k);
                  const isSaved = savedKeys.has(k);
                  const edit = edits[k] || {};

                  return (
                    <TableRow
                      key={k}
                      sx={{
                        bgcolor: isSaved
                          ? "#f0faf4"
                          : isEditing
                            ? "#f0f7ff"
                            : "transparent",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#0054A5",
                        }}
                      >
                        {item.partName}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.pdes}
                      >
                        {item.pdes}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {item.tquant}
                      </TableCell>

                      {/* PRICE — read-only */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.78rem", color: "#1f2937" }}
                        >
                          {formatNum(item.price)}
                        </Typography>
                      </TableCell>

                      {/* NEO_AGENTDISC — read-only text or TextField */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={
                              edit.NEO_AGENTDISC ?? item.neoAgentDisc ?? ""
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                k,
                                "NEO_AGENTDISC",
                                e.target.value,
                              )
                            }
                            inputProps={{
                              style: {
                                fontSize: "0.78rem",
                                padding: "3px 6px",
                                width: 60,
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": { borderRadius: 1 },
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.78rem",
                              color: isSaved ? "#217346" : "#1f2937",
                              fontWeight: isSaved ? 700 : 400,
                            }}
                          >
                            {formatNum(edit.NEO_AGENTDISC ?? item.neoAgentDisc)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Pencil / close toggle */}
                      <TableCell align="center">
                        <Tooltip
                          title={isEditing ? "בטל עריכה" : "ערוך מחיר / הנחה"}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => toggleEdit(k, e)}
                            sx={{
                              color: isEditing ? "#dc2626" : "#6b7280",
                              "&:hover": {
                                color: isEditing ? "#b91c1c" : "#0054A5",
                              },
                            }}
                          >
                            {isEditing ? (
                              <CloseIcon fontSize="small" />
                            ) : (
                              <EditIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {saveError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              {saveError}
            </Typography>
          )}

          {hasDirty && (
            <Box mt={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={saving}
                onClick={handleSave}
                sx={{
                  bgcolor: "#0054A5",
                  borderRadius: 2,
                  fontSize: "0.78rem",
                  "&:hover": { bgcolor: "#003d7a" },
                }}
              >
                {saving ? "שומר..." : "שמור עדכונים ב-Priority"}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const TradeDelayPage = () => {
  const { user } = useUser();
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const {
    orders,
    loading,
    releaseStatuses,
    snackbar,
    setSnackbar,
    updateOrderStatus,
    updateOrderItem,
    refreshOrders,
    fetchLastSales,
    previewing,
    previewData,
    setPreviewData,
    runPreview,
    releasing,
    releaseResult,
    setReleaseResult,
    confirmRelease,
  } = useTradeDelay(execute);

  const isAdmin = user?.roles?.some((r) => r.admin);
  const isSupplier = user?.tradeDelayRole === 'supplier';
  const hasTradeDelayRole = isAdmin || !!user?.tradeDelayRole;
  // External suppliers get a read-only orders list only — no baskets, team,
  // branch-management, or auto-release affordances.
  const showTeamManagement = hasTradeDelayRole && !isSupplier;

  // Branch filter context — fetched once on mount when user has a trade-delay role
  const [branchContext, setBranchContext] = useState(null);
  useEffect(() => {
    if (!hasTradeDelayRole) return;
    execute('GET', `${process.env.REACT_APP_BACKEND_BASE_URL}/api/tradeDelay/branches`)
      .then((res) => setBranchContext(res || null))
      .catch(() => {});
  }, [hasTradeDelayRole, execute]);

  const [tab, setTab] = useState(0);

  // Orders table filters (tab 0)
  const [searchText, setSearchText] = useState("");
  const [dueDateFrom, setDueDateFrom] = useState("");
  const [dueDateTo, setDueDateTo] = useState("");
  const [branchFilter, setBranchFilter] = useState([]);

  // Only branches actually present in the currently loaded orders — no point
  // offering branches with nothing to show.
  const availableBranches = [...new Set(orders.map((o) => o.BRANCHNAME).filter(Boolean))].sort();

  const filteredOrders = orders.filter((order) => {
    if (searchText) {
      const needle = searchText.trim().toLowerCase();
      const haystack = `${order.ORDNAME || ""} ${order.CDES || ""}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }

    if (order.NEO_DUEDATE) {
      const dueDate = new Date(order.NEO_DUEDATE);
      if (dueDateFrom && dueDate < new Date(dueDateFrom)) return false;
      if (dueDateTo && dueDate > new Date(dueDateTo)) return false;
    } else if (dueDateFrom || dueDateTo) {
      return false;
    }

    if (branchFilter.length > 0 && !branchFilter.includes(order.BRANCHNAME)) {
      return false;
    }

    return true;
  });

  const hasActiveFilters = !!searchText || !!dueDateFrom || !!dueDateTo || branchFilter.length > 0;

  const [selectedOrdnames, setSelectedOrdnames] = useState([]);
  // dirtyOrders: Set of ordnames with unsaved edits in preview dialog
  const [dirtyOrders, setDirtyOrders] = useState(new Set());

  const handleDirtyChange = (ordname, isDirty) => {
    setDirtyOrders((prev) => {
      const next = new Set(prev);
      isDirty ? next.add(ordname) : next.delete(ordname);
      return next;
    });
    if (isDirty) {
      setSelectedOrdnames((prev) => prev.filter((o) => o !== ordname));
    }
  };

  const handleClosePreview = () => {
    setPreviewData(null);
    setSelectedOrdnames([]);
    setDirtyOrders(new Set());
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const releasableOrdnames = (previewData?.toRelease || [])
    .filter((r) => !dirtyOrders.has(r.ordname))
    .map((r) => r.ordname);

  return (
    <div className="App">
      <Box p={{ xs: 2, md: 3 }} sx={{ maxWidth: "100%", overflowX: "hidden" }}>
        {/* Header */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              mb: 0,
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,0.08)",
              borderBottom: "none",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "50%",
                  backgroundColor: "rgba(229, 57, 53, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PauseCircleOutlineIcon
                  sx={{ color: "#E53935", fontSize: 32 }}
                />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="#1f2937">
                  שחרור השהיות סחר
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  ניהול הזמנות בסטטוס השהיית סחר
                </Typography>
              </Box>
            </Box>
            {tab === 0 && (
              <Box display="flex" alignItems="center" gap={1.5}>
                {!loading && orders.length > 0 && (
                  <Chip
                    label={
                      hasActiveFilters
                        ? `${filteredOrders.length} מתוך ${orders.length} הזמנות`
                        : `${orders.length} הזמנות`
                    }
                    sx={{
                      bgcolor: "#ebf7ff",
                      color: "#0054A5",
                      fontWeight: 700,
                    }}
                  />
                )}
                {!isSupplier && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      previewing ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <AutoFixHighIcon />
                      )
                    }
                    onClick={runPreview}
                    disabled={previewing || loading}
                    sx={{
                      borderColor: "#217346",
                      color: "#217346",
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#f0faf4", borderColor: "#1a5c38" },
                    }}
                  >
                    {previewing ? "סורק..." : "שחרור אוטומטי"}
                  </Button>
                )}
                <Tooltip title="רענן">
                  <IconButton
                    onClick={refreshOrders}
                    disabled={loading}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Paper>
        </Fade>

        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            border: "1px solid rgba(0,0,0,0.08)",
            borderTop: "none",
            borderBottom: "none",
            mb: 0,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                minHeight: 44,
              },
              "& .Mui-selected": { color: "#0054A5" },
              "& .MuiTabs-indicator": { bgcolor: "#0054A5", height: 3 },
            }}
          >
            <Tab
              icon={<PauseCircleOutlineIcon fontSize="small" />}
              iconPosition="start"
              label="הזמנות בהשהייה"
            />
            {!isSupplier && (
              <Tab
                icon={<ShoppingBasketIcon fontSize="small" />}
                iconPosition="start"
                label="הגדרת סלים"
              />
            )}
            {showTeamManagement && (
              <Tab
                icon={<GroupsIcon fontSize="small" />}
                iconPosition="start"
                label="ניהול צוות"
              />
            )}
            {showTeamManagement && (
              <Tab
                icon={<StoreIcon fontSize="small" />}
                iconPosition="start"
                label="ניהול סניפים"
              />
            )}
          </Tabs>
        </Paper>

        {/* Tab content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            border: "1px solid rgba(0,0,0,0.08)",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            p: { xs: 2, md: 3 },
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
          }}
        >
          {/* Tab 0 — Orders */}
          {tab === 0 && (
            <>
              {/* Branch filter context bar */}
              {branchContext && branchContext.branches && branchContext.branches.length > 0 && (
                <Box
                  mb={2}
                  px={2}
                  py={1.5}
                  sx={{
                    bgcolor: '#f0f7ff',
                    border: '1px solid #bdd7f5',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <StoreIcon sx={{ color: '#0054A5', mt: 0.3, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#0054A5">
                      {user?.tradeDelayRole === 'employee'
                        ? `מציג הזמנות עבור סניפי המנהל: ${branchContext.managerName || ''}`
                        : 'מציג הזמנות עבור הסניפים שלי'}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {branchContext.branches.map((b, i) => (
                        <Chip
                          key={i}
                          label={b}
                          size="small"
                          sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.75rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {!loading && orders.length > 0 && (
                <Box
                  mb={2.5}
                  p={2}
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1.5}
                  sx={{
                    bgcolor: "#f8fafc",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.75} sx={{ color: "#0054A5" }}>
                    <FilterAltIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={700}>
                      סינון
                    </Typography>
                  </Box>

                  <TextField
                    size="small"
                    placeholder="חיפוש לפי מס' הזמנה או שם לקוח"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: 240,
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="אספקה מתאריך"
                    InputLabelProps={{ shrink: true }}
                    value={dueDateFrom}
                    onChange={(e) => setDueDateFrom(e.target.value)}
                    sx={{
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="אספקה עד תאריך"
                    InputLabelProps={{ shrink: true }}
                    value={dueDateTo}
                    onChange={(e) => setDueDateTo(e.target.value)}
                    sx={{
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>סניף</InputLabel>
                    <Select
                      multiple
                      value={branchFilter}
                      onChange={(e) => {
                        const { value } = e.target;
                        setBranchFilter(typeof value === "string" ? value.split(",") : value);
                      }}
                      label="סניף"
                      renderValue={(selected) =>
                        selected.length === 0 ? "" : selected.join(", ")
                      }
                      sx={{ bgcolor: "#ffffff", borderRadius: 2 }}
                    >
                      {availableBranches.map((branch) => (
                        <MenuItem key={branch} value={branch}>
                          <Checkbox size="small" checked={branchFilter.includes(branch)} />
                          <ListItemText primary={branch} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {hasActiveFilters && (
                    <Button
                      size="small"
                      startIcon={<ClearIcon fontSize="small" />}
                      onClick={() => {
                        setSearchText("");
                        setDueDateFrom("");
                        setDueDateTo("");
                        setBranchFilter([]);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#dc2626",
                        "&:hover": { bgcolor: "#fef2f2" },
                      }}
                    >
                      נקה סינון
                    </Button>
                  )}
                </Box>
              )}

              {loading && (
                <Box mb={2}>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                  <Typography variant="body2" color="#6b7280" mt={1}>
                    טוען הזמנות...
                  </Typography>
                </Box>
              )}

              {!loading && orders.length === 0 && (
                <Fade in timeout={600}>
                  <Box textAlign="center" py={6}>
                    <PauseCircleOutlineIcon
                      sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }}
                    />
                    <Typography variant="h6" color="#6b7280">
                      לא נמצאו הזמנות בסטטוס השהיית סחר
                    </Typography>
                  </Box>
                </Fade>
              )}

              {!loading && orders.length > 0 && filteredOrders.length === 0 && (
                <Fade in timeout={600}>
                  <Box textAlign="center" py={6}>
                    <PauseCircleOutlineIcon
                      sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }}
                    />
                    <Typography variant="h6" color="#6b7280">
                      לא נמצאו הזמנות התואמות את הסינון
                    </Typography>
                  </Box>
                </Fade>
              )}

              {!loading && filteredOrders.length > 0 && (
                <Fade in timeout={800}>
                  <Box bgcolor="#fafafa" borderRadius={2} p={2}>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{ borderRadius: 2 }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "#ebf7ff" }}>
                            <TableCell sx={{ width: 48 }} />
                            {[
                              "מס' הזמנה",
                              "לקוח",
                              "תאריך הזמנה",
                              "תאריך אספקה",
                              "סוכן",
                              "חריגת אובליגו",
                              "סטטוס",
                            ].map((col) => (
                              <TableCell
                                key={col}
                                sx={{
                                  fontWeight: "bold",
                                  color: "#1f2937",
                                  py: 1.5,
                                }}
                              >
                                {col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredOrders.map((order) => (
                            <OrderRow
                              key={order.ORDNAME}
                              order={order}
                              releaseStatuses={releaseStatuses}
                              onUpdateStatus={updateOrderStatus}
                              updateOrderItem={updateOrderItem}
                              fetchLastSales={fetchLastSales}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Fade>
              )}
            </>
          )}

          {/* Tab 1 — Baskets */}
          {tab === 1 && <BasketsTab />}

          {/* Tab 2 — Team Management */}
          {tab === 2 && showTeamManagement && (
            <TeamManagementTab currentUser={user} execute={execute} />
          )}

          {/* Tab 3 — Branch Management */}
          {tab === 3 && showTeamManagement && (
            <BranchManagementTab currentUser={user} execute={execute} />
          )}
        </Paper>

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
        />

        {/* Preview Dialog — בחירת הזמנות לשחרור */}
        {previewData && (
          <Dialog
            open
            onClose={handleClosePreview}
            maxWidth="md"
            fullWidth
            dir="rtl"
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <Box sx={{ bgcolor: "#ebf7ff", px: 3, pt: 3, pb: 1.5 }}>
              <DialogTitle sx={{ p: 0 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <AutoFixHighIcon sx={{ color: "#0054A5" }} />
                  <Typography variant="h6" fontWeight="bold" color="#1f2937">
                    שחרור אוטומטי
                  </Typography>
                </Box>
              </DialogTitle>
            </Box>
            <DialogContent sx={{ p: 3 }}>
              {previewData.toRelease?.length > 0 ? (
                <Box>
                  <Typography variant="body2" color="#6b7280" mb={2}>
                    נמצאו {previewData.toRelease.length} הזמנות העומדות בתנאי
                    השחרור. ניתן לערוך מחיר / הנחת סוכן לפני אישור. בחר את
                    ההזמנות לשחרור:
                  </Typography>
                  <Box mb={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            releasableOrdnames.length > 0 &&
                            releasableOrdnames.every((o) =>
                              selectedOrdnames.includes(o),
                            )
                          }
                          indeterminate={
                            selectedOrdnames.length > 0 &&
                            !releasableOrdnames.every((o) =>
                              selectedOrdnames.includes(o),
                            )
                          }
                          onChange={(e) =>
                            setSelectedOrdnames(
                              e.target.checked ? releasableOrdnames : [],
                            )
                          }
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={600}>
                          בחר הכל
                        </Typography>
                      }
                    />
                  </Box>
                  <Box
                    bgcolor="#f8fafc"
                    borderRadius={2}
                    border="1px solid rgba(0,0,0,0.08)"
                    px={1}
                    py={0.5}
                  >
                    <List dense disablePadding>
                      {previewData.toRelease.map((r) => (
                        <PreviewOrderRow
                          key={r.ordname}
                          order={r}
                          checked={selectedOrdnames.includes(r.ordname)}
                          onCheck={(ordname, isChecked) =>
                            setSelectedOrdnames((prev) =>
                              isChecked
                                ? [...prev, ordname]
                                : prev.filter((o) => o !== ordname),
                            )
                          }
                          onDirtyChange={handleDirtyChange}
                          updateOrderItem={updateOrderItem}
                        />
                      ))}
                    </List>
                  </Box>
                </Box>
              ) : (
                <Typography color="#6b7280" textAlign="center" py={2}>
                  אין הזמנות לשחרור אוטומטי
                </Typography>
              )}

              {previewData.skipped?.length > 0 && (
                <Box mt={previewData.toRelease?.length > 0 ? 3 : 1}>
                  <Typography variant="subtitle2" fontWeight={700} color="#6b7280" mb={1}>
                    דולגו {previewData.skipped.length} הזמנות
                  </Typography>
                  <Box
                    bgcolor="#fef2f2"
                    borderRadius={2}
                    border="1px solid #fecaca"
                    px={2}
                    py={1}
                    sx={{ maxHeight: 220, overflowY: "auto" }}
                  >
                    <List dense disablePadding>
                      {previewData.skipped.map((s) => (
                        <ListItem key={s.ordname} disablePadding sx={{ py: 0.3 }}>
                          <ListItemText
                            primary={
                              <>
                                <strong>{s.ordname}</strong>
                                {s.custName && ` (${s.custName})`}: {s.reason}
                              </>
                            }
                            primaryTypographyProps={{
                              fontSize: "0.82rem",
                              color: "#7f1d1d",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                onClick={handleClosePreview}
                sx={{ borderRadius: 2, color: "#6b7280" }}
              >
                ביטול
              </Button>
              {previewData.toRelease?.length > 0 && (
                <Button
                  variant="contained"
                  disabled={
                    selectedOrdnames.length === 0 ||
                    releasing ||
                    dirtyOrders.size > 0
                  }
                  startIcon={
                    releasing ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                  onClick={() =>
                    confirmRelease(
                      previewData.toRelease.filter((r) =>
                        selectedOrdnames.includes(r.ordname),
                      ),
                    )
                  }
                  sx={{
                    bgcolor: "#217346",
                    borderRadius: 2,
                    px: 3,
                    "&:hover": { bgcolor: "#1a5c38" },
                  }}
                >
                  {releasing
                    ? "משחרר..."
                    : `שחרר ${selectedOrdnames.length} הזמנות`}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )}

        {/* Results Dialog — תוצאות לאחר שחרור */}
        {releaseResult && (
          <Dialog
            open
            onClose={() => setReleaseResult(null)}
            maxWidth="sm"
            fullWidth
            dir="rtl"
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <Box sx={{ bgcolor: "#ebf7ff", px: 3, pt: 3, pb: 1.5 }}>
              <DialogTitle sx={{ p: 0 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <AutoFixHighIcon sx={{ color: "#0054A5" }} />
                  <Typography variant="h6" fontWeight="bold" color="#1f2937">
                    תוצאות שחרור אוטומטי
                  </Typography>
                </Box>
              </DialogTitle>
            </Box>
            <DialogContent sx={{ p: 3 }}>
              {releaseResult.released?.length > 0 && (
                <Box mb={releaseResult.failed?.length > 0 ? 2 : 0}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <CheckCircleIcon sx={{ color: "#217346", fontSize: 18 }} />
                    <Typography fontWeight={700} color="#217346">
                      שוחררו {releaseResult.released.length} הזמנות
                    </Typography>
                  </Box>
                  <Box
                    bgcolor="#f0faf4"
                    borderRadius={2}
                    border="1px solid #c6ecd4"
                    px={2}
                    py={1}
                  >
                    <List dense disablePadding>
                      {releaseResult.released.map((r) => (
                        <ListItem
                          key={r.ordname}
                          disablePadding
                          sx={{ py: 0.3 }}
                        >
                          <ListItemText
                            primary={
                              <>
                                <strong>{r.ordname}</strong>
                                {r.custName && ` (${r.custName})`} → {r.newStatus}
                              </>
                            }
                            primaryTypographyProps={{
                              fontSize: "0.85rem",
                              color: "#1f2937",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
              {releaseResult.failed?.length > 0 && (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CancelIcon sx={{ color: "#dc2626", fontSize: 18 }} />
                    <Typography fontWeight={700} color="#dc2626">
                      נכשלו {releaseResult.failed.length} הזמנות
                    </Typography>
                  </Box>
                  <Box
                    bgcolor="#fef2f2"
                    borderRadius={2}
                    border="1px solid #fecaca"
                    px={2}
                    py={1}
                  >
                    <List dense disablePadding>
                      {releaseResult.failed.map((r) => (
                        <ListItem
                          key={r.ordname}
                          disablePadding
                          sx={{ py: 0.3 }}
                        >
                          <ListItemText
                            primary={
                              <>
                                <strong>{r.ordname}</strong>
                                {r.custName && ` (${r.custName})`}: {r.reason}
                              </>
                            }
                            primaryTypographyProps={{
                              fontSize: "0.85rem",
                              color: "#7f1d1d",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setReleaseResult(null)}
                sx={{
                  bgcolor: "#0054A5",
                  color: "#fff",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": { bgcolor: "#003d7a" },
                }}
                variant="contained"
              >
                סגור
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </div>
  );
};

export default TradeDelayPage;
